import { useState, useEffect } from 'react';
import {
  FileText, Sparkles, GraduationCap, MapPin, Award, Target, Briefcase,
  BookOpen, PlusCircle, ChevronLeft, ChevronRight, ListFilter, Brain, Users, Code, Lock, AlertCircle,
} from 'lucide-react';
import { usePortfolio } from '@contexts/PortfolioContext';
import { useReports } from '@contexts/ReportsContext';
import { usePlanRestrictions } from '@hooks/usePlanRestrictions';
import PlanUpgradeModal from '@components/common/PlanUpgradeModal';
import apiService from '@services/api/api.service';
import { getCgpaZone } from '@utils/cgpaZones';
import ReportCard from '@components/reports/ReportCard';

const CareerReport = () => {
  const [profile, setProfile] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [reportFilter, setReportFilter] = useState('active'); // 'active' or 'history'
  const { reports, loading, createNewReport, activeReport } = useReports();
  const { canCreateCareerMap, getRemainingCareerMaps, currentPlan } = usePlanRestrictions();
  const portfolioContext = usePortfolio();
  
  // For locked reports, use activeReport.data directly
  // For editable reports, use PortfolioContext
  const isReportLocked = activeReport && !activeReport.isEditable;
  const reportData = isReportLocked ? activeReport.data : portfolioContext;

  const {
    careerStage = reportData?.careerStage,
    selectedCourse = reportData?.selectedCourse,
    selectedSubjects = reportData?.selectedSubjects || [],
    selectedState = reportData?.selectedState,
    selectedUniversities = reportData?.selectedUniversities || [],
    opportunityType = reportData?.opportunityType,
    selectedPersonalityTraits = reportData?.selectedPersonalityTraits || [],
    selectedCareerDomains = reportData?.selectedCareerDomains || [],
    selectedExams = reportData?.selectedExams || [],
    selectedAptitudeTopics = reportData?.selectedAptitudeTopics || [],
    courses = portfolioContext.courses,
    universities = portfolioContext.universities,
    personalityTraits = portfolioContext.personalityTraits,
    careerDomains = portfolioContext.careerDomains,
    subjects = portfolioContext.subjects,
    // Fresher-specific
    pursuingCourse = reportData?.pursuingCourse,
    courseDuration = reportData?.courseDuration,
    campusName = reportData?.campusName,
    fresherAddress = reportData?.fresherAddress,
    fresherState = reportData?.fresherState,
    fresherDistrict = reportData?.fresherDistrict,
    cgpaRange = reportData?.cgpaRange,
    selectedIndustryOutlook = reportData?.selectedIndustryOutlook || [],
    jobSector = reportData?.jobSector,
    selectedJobs = reportData?.selectedJobs || [],
    resumeReady = reportData?.resumeReady,
    resumeFile = reportData?.resumeFile,
    // Professional-specific
    professionalTier = reportData?.professionalTier,
    industryFocus = reportData?.industryFocus,
    experienceLevel = reportData?.experienceLevel,
    careerSwitchPreferences = reportData?.careerSwitchPreferences || [],
    professionalSoftSkills = reportData?.professionalSoftSkills || [],
    selectedHardSkills = reportData?.selectedHardSkills || [],
  } = isReportLocked ? reportData : portfolioContext;

  const isFresher = careerStage === 'fresher';
  const isProfessional = careerStage === 'professional';

  // Filter reports based on active/history
  const filteredReports = reports.filter(report => {
    if (reportFilter === 'active') {
      return report.isEditable; // Active reports are editable (current plan)
    } else {
      return !report.isEditable; // History reports are non-editable (previous plan)
    }
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await apiService.get('/profile/me');
        if (response.success) {
          setProfile(response?.data?.data);
        }
      } catch (error) {
        console.error('Failed to load profile:', error);
      }
    };
    fetchProfile();
  }, []);
  

  const getCourseName = () => {
    if (!selectedCourse) return '';
    const course = courses.find(c => c.id === selectedCourse);
    return course ? course.name : '';
  };

  const getSubjectNames = () => {
    if (!selectedSubjects || selectedSubjects.length === 0) return '';
    return selectedSubjects.map(subjectId => {
      const subject = subjects.find(s => s.id === subjectId);
      return subject ? subject.name : subjectId;
    }).join(', ');
  };

  const getUniversityNames = () => {
    return selectedUniversities.map(uniId => {
      const uni = universities.find(u => u.id === uniId);
      return uni ? uni.universityName : '';
    }).filter(Boolean);
  };

  const getPersonalityTraitNames = () => {
    return selectedPersonalityTraits.map(traitId => {
      const trait = personalityTraits.find(t => t.id === traitId);
      return trait ? trait.name : '';
    }).filter(Boolean);
  };

  const getCareerDomainNames = () => {
    return selectedCareerDomains.map(domainId => {
      const domain = careerDomains.find(d => d.id === domainId);
      return domain ? domain.name : '';
    }).filter(Boolean);
  };

  const getCompletedSteps = () => {
    let completed = 0;
    if (careerStage) completed++;
    if (selectedCourse) completed++;
    if (selectedSubjects.length > 0) completed++;
    if (selectedState) completed++;
    if (selectedUniversities.length > 0) completed++;
    if (opportunityType) completed++;
    if (selectedPersonalityTraits.length > 0) completed++;
    if (selectedCareerDomains.length > 0) completed++;
    return completed;
  };

  const getFresherCompletedSteps = () => {
    let completed = 0;
    if (careerStage) completed++;
    if (pursuingCourse && courseDuration && campusName) completed++;
    if (fresherAddress && fresherState && fresherDistrict) completed++;
    if (cgpaRange) completed++;
    if (selectedPersonalityTraits.length > 0) completed++;
    if (selectedExams.length > 0 || selectedAptitudeTopics.length > 0) completed++;
    if (jobSector) completed++;
    if (selectedJobs.length > 0 || selectedIndustryOutlook.length > 0) completed++;
    return completed;
  };

  const getProfessionalCompletedSteps = () => {
    let completed = 0;
    if (careerStage) completed++;
    if (professionalTier) completed++;
    if (industryFocus) completed++;
    if (professionalTier === 'Career Switch' && careerSwitchPreferences.length > 0) completed++;
    if (experienceLevel) completed++;
    if (selectedJobs.length > 0) completed++;
    return completed;
  };

  const fresherTotalSteps = 8;
  const professionalTotalSteps = professionalTier === 'Career Switch' ? 6 : 5;
  const studentTotalSteps = 8;
  
  // For locked reports, use activeReport's completion status directly
  // For editable reports, calculate from form data
  const isAllStagesCompleted = isReportLocked 
    ? (activeReport.status === 'completed' || activeReport.completedSteps === activeReport.totalSteps)
    : (isFresher
        ? getFresherCompletedSteps() === fresherTotalSteps
        : isProfessional
        ? getProfessionalCompletedSteps() === professionalTotalSteps
        : getCompletedSteps() === studentTotalSteps);

  const courseName = getCourseName();
  const subjectNames = getSubjectNames();
  const universityNames = getUniversityNames();
  const traitNames = getPersonalityTraitNames();
  const domainNames = getCareerDomainNames();

  return (
    <div className="space-y-3 sm:space-y-4 max-w-full overflow-hidden">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 max-w-full overflow-hidden">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-[#fee2e2] rounded-lg flex-shrink-0">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-[#c33c3d]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight truncate">
              Career Clarity Report
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 truncate">
              Your personalized career journey summary
            </p>
          </div>
        </div>
      </div>

      {/* ── Two-column layout: Reports Panel + Active Report Content ──── */}
      <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 items-start max-w-full">

        {/* Reports Panel (collapsible) */}
        <div
          className={`flex-shrink-0 transition-all duration-300 w-full lg:w-auto ${
            isPanelOpen ? 'lg:w-64 xl:w-72' : 'lg:w-10'
          }`}
        >
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden max-h-[calc(100vh-8rem)] lg:max-h-[calc(100vh-6rem)] flex flex-col">
            {/* Panel header */}
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100">
              {isPanelOpen && (
                <div className="flex items-center gap-1.5 min-w-0">
                  <ListFilter className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                  <span className="text-xs font-semibold text-gray-700 truncate">
                    My Reports
                  </span>
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-bold flex-shrink-0">
                    {reports.length}
                  </span>
                </div>
              )}
              <button
                onClick={() => setIsPanelOpen((p) => !p)}
                className="ml-auto p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
                title={isPanelOpen ? 'Collapse panel' : 'Expand panel'}
              >
                {isPanelOpen
                  ? <ChevronLeft className="w-4 h-4" />
                  : <ChevronRight className="w-4 h-4" />
                }
              </button>
            </div>

            {isPanelOpen && (
              <>
                {/* Filter Tabs */}
                <div className="px-2.5 pt-2.5 pb-2 border-b border-gray-100">
                  <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setReportFilter('active')}
                      className={`flex-1 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all ${
                        reportFilter === 'active'
                          ? 'bg-white text-secondary-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      Active
                      <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[9px] font-bold">
                        {reports.filter(r => r.isEditable).length}
                      </span>
                    </button>
                    <button
                      onClick={() => setReportFilter('history')}
                      className={`flex-1 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all ${
                        reportFilter === 'history'
                          ? 'bg-white text-secondary-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      History
                      <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[9px] font-bold">
                        {reports.filter(r => !r.isEditable).length}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Generate Another Report button */}
                <div className="p-2.5 border-b border-gray-100">
                  {canCreateCareerMap ? (
                    <button
                      onClick={() => createNewReport()}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-secondary-600 hover:bg-secondary-700 text-white text-xs font-semibold transition-colors shadow-sm"
                    >
                      <PlusCircle className="w-3.5 h-3.5" />
                      Generate Another Report
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <button
                        disabled
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gray-300 text-gray-500 text-xs font-semibold cursor-not-allowed"
                      >
                        <Lock className="w-3.5 h-3.5" />
                        Report Limit Reached
                      </button>
                      <button
                        onClick={() => setShowUpgradeModal(true)}
                        className="w-full px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-semibold transition-colors"
                      >
                        Upgrade Plan
                      </button>
                    </div>
                  )}
                </div>

                {/* Reports list */}
                <div className="p-2 space-y-2 flex-1 overflow-y-auto min-h-0">
                  {loading ? (
                    <div className="text-xs text-gray-400 text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600 mx-auto"></div>
                      <p className="mt-2">Loading reports...</p>
                    </div>
                  ) : filteredReports.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">
                      {reportFilter === 'active' ? 'No active reports' : 'No history reports'}
                    </p>
                  ) : (
                    filteredReports.map((report) => (
                      <ReportCard key={report.id} report={report} />
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Active Report Content */}
        <div className="flex-1 w-full lg:min-w-0">

      {/* View-Only Banner */}
      {activeReport && !activeReport.isEditable && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 rounded-xl p-4 mb-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Lock className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                View-Only Report
              </h3>
              <p className="text-sm text-gray-700 mb-3">
                This report is in <span className="font-semibold">view-only mode</span> due to your current plan limits. 
                You can view the Career Clarity Report section, but cannot edit or access other sections.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 lg:p-6 max-w-full overflow-hidden">

      {!isAllStagesCompleted ? (
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-4 sm:p-6 lg:p-8 text-center border border-primary-200">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-white rounded-full flex items-center justify-center shadow-sm">
            <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-navbar" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
            Complete Your Journey
          </h3>
          <p className="text-xs sm:text-base text-gray-600 max-w-md mx-auto px-2">
            Your Career Clarity Report will be generated once you complete all {isFresher ? fresherTotalSteps : isProfessional ? professionalTotalSteps : studentTotalSteps} stages in the Career Roadmap. 
            Keep going, you're on the right path!
          </p>
          <div className="mt-3 sm:mt-4 inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white rounded-lg shadow-sm">
            <span className="text-xl sm:text-2xl font-bold text-navbar">{isFresher ? getFresherCompletedSteps() : isProfessional ? getProfessionalCompletedSteps() : getCompletedSteps()}</span>
            <span className="text-gray-400">/</span>
            <span className="text-sm sm:text-lg text-gray-600">{isFresher ? fresherTotalSteps : isProfessional ? professionalTotalSteps : studentTotalSteps} stages</span>
          </div>
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-3 sm:p-4 lg:p-6 border border-green-100">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                  Congratulations! 🎉
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  You've completed all stages of your career journey
                </p>
              </div>
            </div>
          </div>

          {isFresher ? (
          <div className="space-y-4 sm:space-y-5">
            {/* Personal Information Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 sm:p-6 border-2 border-blue-100 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-md">
                  <GraduationCap className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Personal Information
                </h3>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  Your name is <span className="font-bold text-blue-600">
                    {profile?.firstName && profile?.lastName
                      ? `${profile.firstName} ${profile.lastName}`
                      : profile?.firstName || profile?.lastName || '___________'}
                  </span>. You are a <span className="font-bold text-blue-600">Fresher</span> currently building your career foundation.
                </p>
              </div>
            </div>

            {/* Education Card */}
            {(pursuingCourse || courseDuration || campusName) && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 sm:p-6 border-2 border-purple-100 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center shadow-md">
                    <BookOpen className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                    Education
                  </h3>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    {pursuingCourse && (
                      <>You are pursuing <span className="font-bold text-gray-900">{pursuingCourse}</span></>
                    )}
                    {courseDuration && (
                      <> for a duration of <span className="font-bold text-gray-900">{courseDuration}</span></>
                    )}
                    {campusName && (
                      <> at <span className="font-bold text-gray-900">{campusName}</span></>
                    )}
                    .
                  </p>
                  {cgpaRange && (
                    <>
                      <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                        Your current CGPA range is <span className="font-bold text-gray-900">{cgpaRange}</span>.
                      </p>
                      {(() => {
                        const zone = getCgpaZone(cgpaRange);
                        if (!zone) return null;
                        return (
                          <div className={`mt-2 rounded-xl border-2 ${zone.borderClass} ${zone.bgClass} p-3 sm:p-4`}>
                            <div className="flex items-center gap-2 mb-1.5">
                              <span
                                className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: zone.dotColor }}
                              />
                              <span className={`text-sm sm:text-base font-bold ${zone.textClass}`}>
                                {zone.name}
                              </span>
                              <span className="ml-auto text-[10px] sm:text-xs font-semibold text-gray-600 bg-white px-2 py-0.5 rounded-md border border-gray-200">
                                CGPA {cgpaRange}
                              </span>
                            </div>
                            <p className="text-[11px] sm:text-xs text-gray-700 leading-relaxed">
                              {zone.description}
                            </p>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            )}

              {/* Location */}
              {(fresherAddress || fresherState || fresherDistrict) && (
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-secondary-600" />
                    Location
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You are Campus location based in
                    {fresherDistrict && <> <span className="font-bold text-gray-900">{fresherDistrict}</span></>}
                    {fresherDistrict && fresherState && ', '}
                    {fresherState && <span className="font-bold text-gray-900">{fresherState}</span>}
                    {fresherAddress && (
                      <> at <span className="font-bold text-gray-900">{fresherAddress}</span></>
                    )}
                    .
                  </p>
                </div>
              )}

              {/* Traits */}
              {traitNames.length > 0 && (
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <Award className="w-5 h-5 text-secondary-600" />
                    Personality Traits
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You exhibit traits like{' '}
                    {traitNames.map((trait, index) => (
                      <span key={index}>
                        <span className="font-bold text-gray-900">{trait}</span>
                        {index < traitNames.length - 2 && ', '}
                        {index === traitNames.length - 2 && ' and '}
                      </span>
                    ))}.
                  </p>
                </div>
              )}

              {/* Exams */}
              {selectedExams && selectedExams.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-secondary-600" />
                    Selected Exams ({selectedExams.length})
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You plan to appear for the following exams to advance your career:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {selectedExams.map((exam) => (
                      <div key={exam.id} className="bg-red-50/50 border-2 border-red-200 rounded-xl p-3 sm:p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="text-sm sm:text-base font-bold text-gray-900 leading-snug">
                            {exam.examName}
                            {exam.fullForm && exam.fullForm !== exam.examName && (
                              <span className="block text-xs sm:text-sm text-gray-600 font-normal mt-0.5">
                                ({exam.fullForm})
                              </span>
                            )}
                          </h4>
                        </div>
                        {exam.category && (
                          <p className="text-xs sm:text-sm text-red-600 font-semibold mb-2">
                            {exam.category}
                          </p>
                        )}
                        {exam.purpose && (
                          <div className="mb-2">
                            <p className="text-xs font-semibold text-gray-800 mb-0.5">Purpose:</p>
                            <p className="text-[11px] sm:text-xs text-gray-700 leading-relaxed">
                              {exam.purpose}
                            </p>
                          </div>
                        )}
                        {exam.eligibility && (
                          <div>
                            <p className="text-xs font-semibold text-gray-800 mb-1">Eligibility:</p>
                            <div className="flex flex-wrap gap-1.5">
                              {exam.eligibility
                                .split(/[+,]/)
                                .map((s) => s.trim())
                                .filter(Boolean)
                                .map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-2 py-0.5 rounded-md bg-white border border-red-200 text-red-700 text-[10px] sm:text-[11px] font-medium"
                                  >
                                    {tag}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Aptitude Topics */}
              {selectedAptitudeTopics && selectedAptitudeTopics.length > 0 && (
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <Target className="w-5 h-5 text-secondary-600" />
                    Aptitude Preparation
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You are preparing topics like{' '}
                    {selectedAptitudeTopics.map((t, index) => (
                      <span key={t.id}>
                        <span className="font-bold text-gray-900">{t.topic}</span>
                        {index < selectedAptitudeTopics.length - 2 && ', '}
                        {index === selectedAptitudeTopics.length - 2 && ' and '}
                      </span>
                    ))}
                    .
                  </p>
                </div>
              )}

              {/* Job Sector & Industries */}
              {jobSector && (
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-secondary-600" />
                    Career Direction
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You are targeting <span className="font-bold text-gray-900">{jobSector} Sector Jobs</span>
                    {selectedIndustryOutlook.length > 0 && (
                      <>
                        {' '}with focus on{' '}
                        {selectedIndustryOutlook.map((ind, index) => (
                          <span key={index}>
                            <span className="font-bold text-gray-900">{ind}</span>
                            {index < selectedIndustryOutlook.length - 2 && ', '}
                            {index === selectedIndustryOutlook.length - 2 && ' and '}
                          </span>
                        ))}
                      </>
                    )}
                    .
                  </p>
                </div>
              )}

              {/* Resume Ready */}
              {resumeReady && (
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-secondary-600" />
                    Resume Readiness
                  </h3>
                  {resumeReady === 'yes' ? (
                    <>
                      <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                        Great! You have confirmed that your resume is ready. Having a ready resume puts you ahead in the job application process.
                      </p>
                      {resumeFile && (
                        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-3 sm:p-4 mt-2">
                          <div className="flex items-start gap-2">
                            <FileText className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900">
                                Uploaded Resume: <span className="font-bold text-green-700">{resumeFile.name}</span>
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                File Size: {(resumeFile.size / 1024).toFixed(2)} KB • Uploaded on {new Date(resumeFile.uploadedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-3 sm:p-4">
                      <p className="text-xs sm:text-base text-gray-700 leading-relaxed mb-2">
                        <span className="font-bold text-orange-700">Important Note:</span> You mentioned that your resume is not ready yet.
                      </p>
                      <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                        You might not realize it now, but not having a resume can quietly hold you back. Opportunities don't always come with a warning. A job opening, internship, or referral can appear suddenly—and when it does, the first thing you'll be asked for is your resume. A resume is simply a way to present who you are—your education, your skills, and what you're capable of. You don't need to be perfect. You just need to take the first step.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Selected Jobs */}
              {selectedJobs && selectedJobs.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-secondary-600" />
                    Targeted Job Profiles ({selectedJobs.length})
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You are interested in pursuing the following <span className="font-semibold">{jobSector}</span> sector job roles:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {selectedJobs.map((job) => (
                      <div key={job.id} className="bg-orange-50/50 border-2 border-orange-200 rounded-xl p-3 sm:p-4">
                        <h4 className="text-sm sm:text-base font-bold text-gray-900 leading-snug">
                          {job.title || job.jobName || job.name}
                        </h4>
                        <p className="text-xs sm:text-sm text-orange-600 font-semibold mt-1">
                          {job.sector === 'Government' || jobSector === 'Government'
                            ? `Category: ${job.category || 'N/A'}`
                            : `Industry: ${job.industry || job.category || 'N/A'}`}
                        </p>
                        {job.dept_org && (
                          <div className="mt-2">
                            <p className="text-xs font-semibold text-gray-800 mb-0.5">
                              {job.sector === 'Government' || jobSector === 'Government' ? 'Department:' : 'Organization:'}
                            </p>
                            <p className="text-[11px] sm:text-xs text-gray-700 leading-relaxed">
                              {job.dept_org}
                            </p>
                          </div>
                        )}
                        {job.description && (
                          <div className="mt-2">
                            <p className="text-xs font-semibold text-gray-800 mb-0.5">Role Description:</p>
                            <p className="text-[11px] sm:text-xs text-gray-700 leading-relaxed">
                              {job.description}
                            </p>
                          </div>
                        )}
                        {job.skills && Array.isArray(job.skills) && job.skills.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-semibold text-gray-800 mb-1">Required Skills:</p>
                            <div className="flex flex-wrap gap-1.5">
                              {job.skills.map((skill, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-0.5 rounded-md bg-white border border-orange-200 text-orange-700 text-[10px] sm:text-[11px] font-medium"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
          ) : isProfessional ? (
          <div className="bg-white rounded-xl p-4 sm:p-6 lg:p-8 border border-gray-200 max-w-full overflow-hidden">
            <div className="space-y-4 sm:space-y-6">
              {/* Personal Information */}
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-secondary-600" />
                  Personal Information
                </h3>
                <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                  Your name is <span className="font-bold text-gray-900">
                    {profile?.firstName && profile?.lastName
                      ? `${profile.firstName} ${profile.lastName}`
                      : profile?.firstName || profile?.lastName || '___________'}
                  </span>. You are a <span className="font-bold text-gray-900">Working Professional</span> focused on career growth and advancement.
                </p>
              </div>

              {/* Professional Profile */}
              {professionalTier && (
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-secondary-600" />
                    Professional Profile
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You are currently at the <span className="font-bold text-gray-900">{professionalTier}</span> stage of your professional journey
                    {experienceLevel && (
                      <> with <span className="font-bold text-gray-900">{experienceLevel}</span> of experience</>
                    )}
                    {industryFocus && (
                      <>, specializing in <span className="font-bold text-gray-900">{industryFocus}</span></>
                    )}
                    .
                  </p>
                </div>
              )}

              {/* Career Switch Preferences */}
              {professionalTier === 'Career Switch' && careerSwitchPreferences.length > 0 && (
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <Target className="w-5 h-5 text-secondary-600" />
                    Career Switch Motivations
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed mb-2">
                    You are looking to switch careers for the following reasons:
                  </p>
                  <ul className="list-disc list-inside space-y-1.5 text-xs sm:text-base text-gray-700">
                    {careerSwitchPreferences.map((pref, index) => (
                      <li key={index} className="leading-relaxed">{pref}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Targeted Job Profiles */}
              {selectedJobs && selectedJobs.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-secondary-600" />
                    Targeted Job Profiles ({selectedJobs.length})
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You are interested in pursuing the following professional roles:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {selectedJobs.map((job) => (
                      <div key={job.id} className="bg-blue-50/50 border-2 border-blue-200 rounded-xl p-3 sm:p-4">
                        <h4 className="text-sm sm:text-base font-bold text-gray-900 leading-snug">
                          {job.role || job.title || job.jobName}
                        </h4>
                        {job.experience && (
                          <p className="text-xs sm:text-sm text-blue-600 font-semibold mt-1">
                            Experience: {job.experience}
                          </p>
                        )}
                        {job.level && (
                          <p className="text-xs sm:text-sm text-blue-600 font-semibold mt-1">
                            Level: {job.level}
                          </p>
                        )}
                        {job.skills && (
                          <div className="mt-2">
                            <p className="text-xs font-semibold text-gray-800 mb-0.5">Skills:</p>
                            <p className="text-[11px] sm:text-xs text-gray-700 leading-relaxed">
                              {job.skills}
                            </p>
                          </div>
                        )}
                        {job.tools && (
                          <div className="mt-2">
                            <p className="text-xs font-semibold text-gray-800 mb-0.5">Tools:</p>
                            <p className="text-[11px] sm:text-xs text-gray-700 leading-relaxed">
                              {job.tools}
                            </p>
                          </div>
                        )}
                        {job.useCase && (
                          <div className="mt-2">
                            <p className="text-xs font-semibold text-gray-800 mb-0.5">Use Case:</p>
                            <p className="text-[11px] sm:text-xs text-gray-700 leading-relaxed">
                              {job.useCase}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Learner Characteristics (Personality Traits) */}
              {selectedPersonalityTraits.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-600" />
                    Learner Characteristics ({selectedPersonalityTraits.length})
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    Your personality traits that define your professional approach:
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedPersonalityTraits.map((trait) => (
                      <span
                        key={trait.id}
                        className="inline-flex items-center px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-xs text-purple-700 font-medium"
                      >
                        {trait.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Professional Soft Skills */}
              {professionalSoftSkills.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Professional Soft Skills ({professionalSoftSkills.length})
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    Key soft skills you possess for professional success:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {professionalSoftSkills.map((skill) => (
                      <div key={skill.id} className="bg-blue-50/50 border border-blue-200 rounded-lg p-3">
                        <h4 className="text-sm font-bold text-gray-900 mb-1">{skill.skillName}</h4>
                        <p className="text-xs text-gray-700">{skill.definition}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hard Skills */}
              {selectedHardSkills.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2 flex items-center gap-2">
                    <Code className="w-5 h-5 text-green-600" />
                    Hard Skills ({selectedHardSkills.length})
                  </h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    Your technical and domain-specific skills:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    {selectedHardSkills.map((skill) => (
                      <div key={skill.id} className="bg-green-50/50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="text-sm font-bold text-gray-900">{skill.name}</h4>
                          <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            {skill.category}
                          </span>
                        </div>
                        {skill.definition && (
                          <p className="text-xs text-gray-700">{skill.definition}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          ) : (
          <div className="bg-white rounded-xl p-4 sm:p-6 lg:p-8 border border-gray-200 max-w-full overflow-hidden">
            <div className="space-y-4 sm:space-y-6">
              {/* Personal Information */}
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2">Personal Information</h3>
                <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                  Your name is <span className="font-bold text-gray-900">
                    {profile?.firstName && profile?.lastName 
                      ? `${profile.firstName} ${profile.lastName}`
                      : profile?.firstName || profile?.lastName || '___________'}
                  </span>.
                </p>
                <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                  You have selected <span className="font-bold text-gray-900">
                    {selectedState || '___________'} state for Higher education
                  </span>.
                </p>
              </div>

              {/* Traits */}
              {traitNames.length > 0 && (
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2">Traits</h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You exhibit traits like{' '}
                    {traitNames.map((trait, index) => (
                      <span key={index}>
                        <span className="font-bold text-gray-900">{trait}</span>
                        {index < traitNames.length - 2 && ', '}
                        {index === traitNames.length - 2 && ' and '}
                      </span>
                    ))}.
                  </p>
                </div>
              )}

              {/* Education */}
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2">Education</h3>
                {universityNames.length > 0 && (
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You wish to pursue education at{' '}
                    {universityNames.map((uni, index) => (
                      <span key={index}>
                        <span className="font-bold text-gray-900">{uni}</span>
                        {index < universityNames.length - 2 && ', '}
                        {index === universityNames.length - 2 && ' and '}
                      </span>
                    ))}
                    {universityNames.length > 0 && '.'}
                  </p>
                )}
                {courseName && (
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You have chosen <span className="font-bold text-gray-900">{courseName}</span>
                    {subjectNames && (
                      <span>
                        {' '}with subjects like{' '}
                        {selectedSubjects.map((subjectId, index) => {
                          const subject = subjects.find(s => s.id === subjectId);
                          return subject ? (
                            <span key={index}>
                              <span className="font-bold text-gray-900">{subject.name}</span>
                              {index < selectedSubjects.length - 2 && ', '}
                              {index === selectedSubjects.length - 2 && ' and '}
                            </span>
                          ) : null;
                        })}
                      </span>
                    )}.
                  </p>
                )}
              </div>

              {/* Exams */}
              {selectedExams && selectedExams.length > 0 && (
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2">Exams</h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You plan to appear for{' '}
                    {selectedExams.map((exam, index) => (
                      <span key={exam.id}>
                        <span className="font-bold text-gray-900">
                          {exam.examName}
                          {exam.fullForm ? ` (${exam.fullForm})` : ''}
                        </span>
                        {index < selectedExams.length - 2 && ', '}
                        {index === selectedExams.length - 2 && ' and '}
                      </span>
                    ))}
                    .
                  </p>
                </div>
              )}

              {/* Aptitude Preparation */}
              {selectedAptitudeTopics && selectedAptitudeTopics.length > 0 && (
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2">Aptitude Preparation</h3>
                  <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                    You are preparing topics like{' '}
                    {selectedAptitudeTopics.map((t, index) => (
                      <span key={t.id}>
                        <span className="font-bold text-gray-900">{t.topic}</span>
                        {index < selectedAptitudeTopics.length - 2 && ', '}
                        {index === selectedAptitudeTopics.length - 2 && ' and '}
                      </span>
                    ))}
                    .
                  </p>
                </div>
              )}

              {/* Career Goals */}
              {(domainNames.length > 0 || opportunityType) && (
                <div className="space-y-2 sm:space-y-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 border-b pb-2">Career Goals</h3>
                  {domainNames.length > 0 && opportunityType && (
                    <p className="text-xs sm:text-base text-gray-700 leading-relaxed">
                      You aim to build a career in{' '}
                      {domainNames.map((domain, index) => (
                        <span key={index}>
                          <span className="font-bold text-gray-900">{domain}</span>
                          {index < domainNames.length - 2 && ', '}
                          {index === domainNames.length - 2 && ' and '}
                        </span>
                      ))}
                      {' '}and prefer working in the{' '}
                      <span className="font-bold text-gray-900">
                        {opportunityType === 'private' ? 'Private sector' : 'Government sector'}
                      </span>.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Your Career Summary
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {careerStage && (
                <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                  <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Career Stage</p>
                  <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                    {careerStage.charAt(0).toUpperCase() + careerStage.slice(1)}
                  </p>
                </div>
              )}
              {isFresher ? (
                <>
                  {pursuingCourse && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Pursuing Course</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{pursuingCourse}</p>
                    </div>
                  )}
                  {campusName && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Campus</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{campusName}</p>
                    </div>
                  )}
                  {cgpaRange && (() => {
                    const zone = getCgpaZone(cgpaRange);
                    return (
                      <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                        <p className="text-[10px] sm:text-xs text-gray-500 mb-1">CGPA Range</p>
                        <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{cgpaRange}</p>
                        {zone && (
                          <span className="inline-flex items-center gap-1 mt-1 text-[10px] sm:text-xs font-semibold" style={{ color: zone.dotColor }}>
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: zone.dotColor }} />
                            {zone.name}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {fresherState && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Location</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                        {fresherDistrict ? `${fresherDistrict}, ${fresherState}` : fresherState}
                      </p>
                    </div>
                  )}
                  {jobSector && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Job Sector</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{jobSector}</p>
                    </div>
                  )}
                  {selectedExams.length > 0 && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Exams Selected</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{selectedExams.length} exam{selectedExams.length !== 1 ? 's' : ''}</p>
                    </div>
                  )}
                  {selectedJobs.length > 0 && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Jobs Targeted</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{selectedJobs.length} role{selectedJobs.length !== 1 ? 's' : ''}</p>
                    </div>
                  )}
                  {resumeReady && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Resume Ready</p>
                      <p className={`text-sm sm:text-base font-medium ${
                        resumeReady === 'yes' ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {resumeReady === 'yes' ? 'Yes' : 'No'}
                      </p>
                      {resumeFile && resumeReady === 'yes' && (
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-1 truncate" title={resumeFile.name}>
                          {resumeFile.name}
                        </p>
                      )}
                    </div>
                  )}
                </>
              ) : isProfessional ? (
                <>
                  {professionalTier && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Professional Tier</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{professionalTier}</p>
                    </div>
                  )}
                  {industryFocus && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Industry Focus</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{industryFocus}</p>
                    </div>
                  )}
                  {experienceLevel && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Experience Level</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{experienceLevel}</p>
                    </div>
                  )}
                  {careerSwitchPreferences.length > 0 && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Career Switch Reasons</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{careerSwitchPreferences.length} selected</p>
                    </div>
                  )}
                  {selectedJobs.length > 0 && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Jobs Targeted</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{selectedJobs.length} role{selectedJobs.length !== 1 ? 's' : ''}</p>
                    </div>
                  )}
                  {resumeReady && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Resume Ready</p>
                      <p className={`text-sm sm:text-base font-medium ${
                        resumeReady === 'yes' ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {resumeReady === 'yes' ? 'Yes' : 'No'}
                      </p>
                      {resumeFile && resumeReady === 'yes' && (
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-1 truncate" title={resumeFile.name}>
                          {resumeFile.name}
                        </p>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {courseName && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Course</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{courseName}</p>
                    </div>
                  )}
                  {selectedState && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Preferred State</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">{selectedState}</p>
                    </div>
                  )}
                  {opportunityType && (
                    <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-1">Opportunity Type</p>
                      <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
                        {opportunityType === 'private' ? 'Private Sector' : 'Government Sector'}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </div>

        </div>
        {/* ── end Active Report Content ── */}
      </div>
      {/* ── end Two-column layout ── */}

      {/* Plan Upgrade Modal */}
      <PlanUpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />

    </div>
  );
};

export default CareerReport;
